import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { DeviceEventEmitter, FlatList, ScrollView, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { widgetDisplayType } from '../../components/widgetTypes/getWidget';
import { ImageComp } from '../../components/Image/Image';
import { getCleanUrl, toBool } from '../../utils/Helper';
import { useCssStyle } from '../../customHook/getCssStyle';
import useWidgetClickHandler from '../../customHook/useWidgetClickHandler';
import Header from '../../components/Header/Header';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import { EARLY_CHECKIN, ISINROOMTAB, MOBILE_CHECKIN, myStayTab, PRE_CHECKIN } from '../../config/Constant';
import CustomFlatList from '../../components/customFlatList';
import PromoWidget from '../promoWidget/promoWidget';
import { formatTime } from '../../js/formatTime';
import CustomPressable from '../../components/customPressable/customPressable';
import { setShowLoginModal } from '../../Redux/Reducers/appConfigReducer';
import { setGuestRoomData } from '../../Redux/Reducers/guestRoomReducer';
import ParallaxScrollView from '../../components/ParallaxScrollView';

const HomePage = props => {
  const { tabDetails, widgetList } = props || {};

  const { t } = useTranslation();
  const getStyles = useCssStyle();
  const dispatch = useDispatch();

  const mainScrollRef = useRef(null);
  const mainEventListener = useRef(null);

  const { webAppWidgetsInfo, currentLayout } = useSelector(state => state.deviceConfigSlice);
  const { allowUserLoginViaFindingReservation } = webAppWidgetsInfo?.appConfigs || {};
  const { preCheckInWidgetState } = useSelector(state => state.checkInSlice);
   const { preCheckInCompleted, preCheckInData } = useSelector(state => state.checkInSlice.preCheckInStatus);
  const { isBookingFound, bookingData } = useSelector(state => state.checkInSlice.bookingStatus);

  const { isLoggedIn, loggedInRoomNumber } = useSelector(state => state.appConfigSlice)
  const { currentGuestDetails } = useSelector(state => state.guestDetailsSlice);
  
  const tabImgUrl = getCleanUrl(currentLayout?.brandingInfo?.launcherBgImageUrl)


  const { mobileCheckInConfigs } = webAppWidgetsInfo || {};
  const TIME_FORMAT = useSelector(state => state.deviceConfigSlice?.extraConfigParam?.TIME_FORMAT_MERIDIEM) || false;
  const checkinStartTime = formatTime(mobileCheckInConfigs.checkInStartTime, toBool(TIME_FORMAT));
  const earlyCheckInStartTime = formatTime(mobileCheckInConfigs.earlyCheckInStartTime, toBool(TIME_FORMAT))


  // Using the custom hook to handle widget clicks
  const handleOnWidgetClick = useWidgetClickHandler();



  const handleOpenLogout = () => {
      dispatch(setGuestRoomData({
          activeState: 'show_logout'
      }));
      dispatch(setShowLoginModal(true));
  }




  useEffect(() => {
    if (!mainEventListener?.current) {
      mainEventListener.current = DeviceEventEmitter.addListener('scrollToTop', (eventTabDetails) => {
        if (eventTabDetails.uniqueKey === tabDetails.uniqueKey) {
          mainScrollRef?.current?.scrollToOffset?.({ y: 0, animated: true });
        }
      });
    }
  
    return () => {
      mainEventListener?.current?.remove();
      mainEventListener.current = null;
    };
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const renderHeader = useCallback(() => (
    <>
      <View style={getStyles(['mainContainerHeaderContainer', `mainContainerHeaderContainer_${tabDetails.uniqueKey}`])} id={`mainContainerHeaderContainer mainContainerHeaderContainer_${tabDetails.uniqueKey}`} >
        <ImageComp source={tabImgUrl} style={getStyles(['mainContainerHeaderImg', `mainContainerHeaderImg_${tabDetails.uniqueKey}`])} id={`mainContainerHeaderImg mainContainerHeaderImg_${tabDetails.uniqueKey}`} />
        <View style={getStyles(['mainContainerHeaderTitleContainer', `mainContainerHeaderTitleContainer_${tabDetails.uniqueKey}`])} id={`mainContainerHeaderTitleContainer mainContainerHeaderTitleContainer_${tabDetails.uniqueKey}`}>
          {tabDetails?.tabName && <Text style={getStyles(['mainContainerHeaderTitle', `mainContainerHeaderTitle_${tabDetails.uniqueKey}`])} id={`mainContainerHeaderTitle mainContainerHeaderTitle_${tabDetails.uniqueKey}`} >{tabDetails?.tabName}</Text>}
        </View>
      </View>
      <PromoWidget />
    </>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ),[tabDetails, tabImgUrl]);


  const renderLogoutButton = () => (!ISINROOMTAB && (isLoggedIn || isBookingFound) && myStayTab.includes(tabDetails?.uniqueKey)) ? (
    <View style={getStyles(['mainContainer_logout_button'])} >
      <CustomPressable style={getStyles(['secondaryBtn'])} onPress={handleOpenLogout} >
        <Text style={getStyles(['secondaryBtnTxt'])}>{t('logout.logout_button')}</Text>
      </CustomPressable>
    </View>
  ) : null


  const renderItem = ({ item, index }) => {
    const { subWidgetMap, displayName } = item;
    let currentWidgeDisplayType = item.widgetType;

    let updatedSubWidgetMap = [...subWidgetMap];

    // updating guest details widget
    const guestDetailsIndex = updatedSubWidgetMap.findIndex((obj) => obj.widgetType == "Guest Details")

    if (guestDetailsIndex !== -1) {
      currentWidgeDisplayType = "Guest Details";
    }


    // updating Guest Itinerary Widget
    const guestItineraryIndex = updatedSubWidgetMap.findIndex((obj) => obj.widgetType == "Guest Itinerary")

    if(guestItineraryIndex !== -1) {
      currentWidgeDisplayType = "Guest Itinerary";
    }



    // updating Pre Check-in widget as per state:
    const preCheckInIndex = updatedSubWidgetMap.findIndex((obj) => obj.widgetType === "Pre Check-in");

    if (preCheckInIndex != -1) {

      if(isLoggedIn || isBookingFound){
        updatedSubWidgetMap.splice(preCheckInIndex, 1);
      } else {
        let pre_checkin_widget = { ...updatedSubWidgetMap[preCheckInIndex] };
        const widgetTitles = {
          [PRE_CHECKIN]: t('pre_checkIn.pre_checkin_widget_name'),
          [EARLY_CHECKIN]: t('check_in.early_checkin_widget_name'),
          [MOBILE_CHECKIN]: t('check_in.checkin_widget_name'),
        };

        // Update the widget title and type
        pre_checkin_widget.widgetTitle = widgetTitles[preCheckInWidgetState] || pre_checkin_widget.widgetTitle;
        pre_checkin_widget.widgetType = preCheckInWidgetState;

        updatedSubWidgetMap[preCheckInIndex] = pre_checkin_widget;
      }
    }


    // updating Pre Check-in widget as per login state:
    const mobileCheckInIndex = updatedSubWidgetMap.findIndex((obj) => obj.widgetType === "Mobile Check-in");
    if(mobileCheckInIndex != -1) {
      if(isLoggedIn || isBookingFound){
        updatedSubWidgetMap.splice(mobileCheckInIndex, 1);
      }
    }

    const isHomescreenIframe =  updatedSubWidgetMap.find((obj) => obj.widgetType == "URL iFrame Renderer (Open Default)");
    if(isHomescreenIframe){
      currentWidgeDisplayType = "URL iFrame Renderer (Open Default)";
    }



    const matchingComponent = widgetDisplayType.find(
      widget => widget?.type === currentWidgeDisplayType,
    );
    if (!matchingComponent) {
      return null;
    }

    const WidgetComponent = matchingComponent.CompName;

    if(updatedSubWidgetMap.length <= 0){
      return null;
    }

    return (
        <WidgetComponent
          handleOnWidgetClick={handleOnWidgetClick}
          listItemData={updatedSubWidgetMap}
          widget={item}
        />
    );
  };

  return (
    <View style={getStyles(['mainRootContainer'])} id='mainRootContainer' >
      <Header />

      <View style={getStyles(['mainContainer'])} id='mainContainer'>
        <CustomFlatList
          ref={mainScrollRef}
          data={widgetList}
          ListHeaderComponent={renderHeader}
          ListFooterComponent={renderLogoutButton}
          renderItem={renderItem}
          contentContainerStyle={getStyles(['mainContainerWidgetsContainer'])}
          keyExtractor={(item, index) => `${item.widgetType}-${index}`}
        />
      </View>
    </View>
  );
};

export default memo(HomePage);
